import { useState } from 'react';

interface ManageProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: {
    id: string;
    name: string;
    whitelisted_users: number;
  };
}

export default function ManageProjectModal({ isOpen, onClose, project }: ManageProjectModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if it's a .lua file
    if (!file.name.endsWith('.lua')) {
      setError('Only .lua files are allowed');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`/api/projects/${project.id}/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to upload file');
      }

      setSuccess('File uploaded successfully');
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-white mb-4">Manage Project: {project.name}</h2>
        
        <div className="space-y-4">
          <div className="bg-gray-700 p-4 rounded">
            <h3 className="text-white font-semibold mb-2">Project Details</h3>
            <p className="text-gray-300">Whitelisted Users: {project.whitelisted_users}</p>
          </div>

          <div className="bg-gray-700 p-4 rounded">
            <h3 className="text-white font-semibold mb-2">Script Upload</h3>
            <div className="space-y-2">
              <label className="block">
                <span className="sr-only">Choose main.lua file</span>
                <input
                  type="file"
                  accept=".lua"
                  onChange={handleFileUpload}
                  disabled={isLoading}
                  className="block w-full text-sm text-gray-300
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-indigo-600 file:text-white
                    hover:file:bg-indigo-700
                    disabled:opacity-50"
                />
              </label>
              {isLoading && (
                <p className="text-gray-400">Uploading...</p>
              )}
              {error && (
                <p className="text-red-500">{error}</p>
              )}
              {success && (
                <p className="text-green-500">{success}</p>
              )}
            </div>
          </div>

          <div className="bg-gray-700 p-4 rounded">
            <h3 className="text-white font-semibold mb-2">Discord Integration</h3>
            <p className="text-gray-300">Coming soon...</p>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-300 hover:text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
} 